import React from 'react';
import { Box, ButtonBase } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Analysis, Issue } from './ganttAnalyze';
import { ATTENTION_CHECKS_ENABLED, TOKENS } from './ganttConfig';
import { fmtClock } from './GanttBar';

interface Props {
  analysis: Analysis;
  onJump: (iss: Issue) => void;
}

function whenLabel(iss: Issue): string {
  if (iss.day === 0) return fmtClock(iss.absStart % 1440);
  if (iss.day === 1) return 'Tomorrow';
  return `+${iss.day}d`;
}

export default function AttentionPanel({ analysis, onJump }: Props) {
  const { issues, counts } = analysis;
  return (
    <Box sx={{ width: 340 }}>
      <Box sx={{ px: 1.5, pt: 1.25, pb: 1, borderBottom: `1px solid ${TOKENS.lineSoft}` }}>
        <Box sx={{ fontSize: 14, fontWeight: 600, color: TOKENS.ink }}>Needs attention</Box>
        {ATTENTION_CHECKS_ENABLED && (
          <Box sx={{ fontSize: 11, color: TOKENS.ink3, mt: 0.25 }}>
            {counts.overlap} double-booked · {counts.overbooked} overbooked
          </Box>
        )}
      </Box>
      <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
        {/* The gate is per-check now (see analyze): with it down, `passweld` still
            reports. So the "nothing is running" copy is only right when there is
            genuinely nothing to show. */}
        {!ATTENTION_CHECKS_ENABLED && issues.length === 0 && (
          <Box sx={{ p: 2, fontSize: 12.5, color: TOKENS.ink3, textAlign: 'center', lineHeight: 1.5 }}>
            No automatic checks are running.<br />This is where scheduling alerts will land.
          </Box>
        )}
        {ATTENTION_CHECKS_ENABLED && issues.length === 0 && (
          <Box sx={{ p: 2, fontSize: 12.5, color: TOKENS.ink3, textAlign: 'center' }}>All clear — nothing needs a decision.</Box>
        )}
        {issues.map((iss, i) => {
          const isOverlap = iss.kind === 'overlap';
          return (
            <ButtonBase
              key={`${iss.id}-${iss.kind}-${i}`}
              onClick={() => onJump(iss)}
              sx={{ display: 'flex', width: '100%', textAlign: 'left', alignItems: 'center', gap: 1.25, px: 1.5, py: 1, borderBottom: `1px solid ${TOKENS.lineSoft}`, '&:hover': { background: TOKENS.bg } }}
            >
              <Box sx={{
                width: 26, height: 26, flexShrink: 0, borderRadius: '7px', display: 'grid', placeItems: 'center',
                color: '#fff', background: isOverlap ? TOKENS.bad : TOKENS.warn,
              }}>
                {isOverlap ? <WarningAmberRoundedIcon sx={{ fontSize: 15 }} /> : <AccessTimeIcon sx={{ fontSize: 15 }} />}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontSize: 12.5, fontWeight: 600, color: TOKENS.ink }}>{iss.label}</Box>
                <Box sx={{ fontSize: 11, color: TOKENS.ink3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {iss.press} · {iss.job} {iss.cust} · {iss.desc}
                </Box>
              </Box>
              <Box sx={{ fontSize: 11, color: TOKENS.ink3, fontFamily: TOKENS.monoFont, flexShrink: 0 }}>{whenLabel(iss)}</Box>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}
